import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Link,
  Tooltip,
  Stack,
  Typography,
  useTheme,
  Box,
  Card,
  CardContent,
  CardActions,
  Grid,
  CardHeader,
  Avatar,
  Pagination,
  useMediaQuery,
  Popover,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchGet } from '../../api/common';
import { track } from '../../api/track';
import {
  CATEGORY_SETS_LABEL,
  MODE_ABBR,
  MODE_DISPLAY_NAME,
} from '../../common/constants';
import { ScoreCardModal } from '../../modals/ScoreCardModal';
import { calcMatchRating } from '../../services/mmr-calc';
import {
  Player,
  Mode,
  DetailedPlayer,
  DbQuestionData,
  WhosOnline,
} from '../room/types';
import { assignRanks, utcToLocalDateTime } from '../room/utils';
import DoneIcon from '@mui/icons-material/Done';
import { BiGhost } from 'react-icons/bi';
import { isMobile } from 'react-device-detect';
import { HelpfulTipsDialog } from '../../dialog/HelpfulTipsDialog';
import { FaSeedling } from 'react-icons/fa';
import SearchIcon from '@mui/icons-material/Search';
import { JoinedPlayersMorePopover } from './JoinedPlayersMorePopover';
import { GiDevilMask, GiRaiseZombie, GiStoneTower } from 'react-icons/gi';
import BalanceIcon from '@mui/icons-material/Balance';
import { Game } from '@par/shared/types/game';
import { Profile } from '@par/shared/types/player';
import { Socket } from 'socket.io-client';
import { PlayerStatusesDialog } from '../../dialog/PlayerStatusesDialog';

export const MAX_PLAYERS_IN_TEAMS = 8;
let joinTeamButtonTimeout;

type PropTypes = {
  handleForceStartClick: (game: Game) => void;
  handleJoinGameClick: (gameId: string, mode: Mode) => void;
  handleLeaveGameClick: (gameId: string, profileId: string) => void;
  handlePickTeamsClick: (gameId: string) => void;
  handleRemoveGameClick: (game: Game) => void;
  handleRejoinGameClick: (gameId: string) => void;
  handleSpectateClick: (gameId: string) => void;
  lobbyGames?: Game[];
  onlinePlayer: WhosOnline;
  openTeamPickingDialog?: boolean;
  profile: Profile;
  socket: Socket;
};

export const LobbyTable = ({
  handleForceStartClick,
  handleJoinGameClick,
  handleLeaveGameClick,
  handlePickTeamsClick,
  handleRemoveGameClick,
  handleRejoinGameClick,
  handleSpectateClick,
  lobbyGames,
  onlinePlayer,
  openTeamPickingDialog,
  profile: profileMe,
  socket,
}: PropTypes) => {
  const navigate = useNavigate();
  const [openScorecardModal, setOpenScorecardModal] = useState(false);
  const [gameResults, setGameResults] = useState<
    Game & { questionSet: DbQuestionData[] }
  >();
  const [pageNum, setPageNum] = useState(1);
  const [lobbyGamesPaged, setLobbyGamesPaged] = useState<Game[]>();
  const [noGamesToDisplay, setNoGamesToDisplay] = useState(false);
  const [clickedJoinedPlayersMore, setClickedJoinedPlayersMore] =
    useState<any>();
  const [openJoinedPlayersMorePopover, setOpenJoinedPlayersMorePopover] =
    useState(false);
  const [anchorElJoinedPlayersMore, setAnchorElJoinedPlayersMore] =
    useState<HTMLButtonElement | null>(null);
  const [disabledTeamJoinButton, setDisabledTeamJoinButton] = useState(false);

  const theme = useTheme();
  const xsBreakpoint = useMediaQuery(theme.breakpoints.only('xs'));

  useEffect(() => {
    const start = 10 * pageNum - 10;
    const end = 10 * pageNum;
    setLobbyGamesPaged(lobbyGames?.slice(start, end));
  }, [lobbyGames, pageNum]);

  useEffect(() => {
    if (!lobbyGamesPaged) return;
    if (!lobbyGamesPaged.length) {
      setNoGamesToDisplay(true);
    } else {
      setNoGamesToDisplay(false);
    }
  }, [lobbyGamesPaged]);

  const joinTeamGameClick = (gameId: string, mode: Mode) => {
    clearTimeout(joinTeamButtonTimeout);
    setDisabledTeamJoinButton(true);
    joinTeamButtonTimeout = setTimeout(() => {
      setDisabledTeamJoinButton(false);
    }, 2000);
    handleJoinGameClick(gameId, mode);
  };

  const handleResetPlayers = (activePlayers: string[]) => {
    for (const profileId of activePlayers) {
      socket.emit('resetPlayerStatus', {
        profileId: profileId,
      });
    }
  };

  const handlePlayerStatusesClick = () => {
    console.log('ap', adminClickedActivePlayers);
    setOpenPlayerStatusesDialog(true);
  };

  const actionButton = (r: Game) => {
    if (r.gameOver) return null;
    if (
      !isMobile &&
      onlinePlayer.gameInProgress &&
      r.activePlayers.includes(profileMe.id) &&
      r.gameStarted
    ) {
      return (
        <Button
          variant="contained"
          color="warning"
          onClick={() => handleRejoinGameClick(r.gameId)}
        >
          REJOIN
        </Button>
      );
    }
    if (
      !isMobile &&
      r.mode === 'match' &&
      !r.activePlayers.includes(profileMe.id) &&
      r.activePlayers.length !== 2
    ) {
      return (
        <Stack direction={'row'} spacing={1}>
          <Button
            variant="contained"
            sx={{ width: 80 }}
            onClick={() => handleJoinGameClick(r.gameId, r.mode)}
          >
            Join
          </Button>
          {r.createdBy === profileMe.id ? (
            <Button
              variant="contained"
              sx={{ width: 80 }}
              onClick={() => handleRemoveGameClick(r)}
            >
              Remove
            </Button>
          ) : null}
        </Stack>
      );
    }
    if (
      !isMobile &&
      r.mode === 'sit-n-go' &&
      !r.gameStarted &&
      !r.activePlayers.includes(profileMe.id) &&
      !r.confirmingPlayers &&
      r.activePlayers.length < r.sitAndGoMaxPlayers
    ) {
      return (
        <Stack direction={'row'} spacing={1}>
          <Button
            variant="contained"
            sx={{ width: 80 }}
            onClick={() => handleJoinGameClick(r.gameId, r.mode)}
          >
            Join
          </Button>
          {r.activePlayers.length === 0 && r.createdBy === profileMe.id ? (
            <Button
              sx={{ width: 80 }}
              variant="contained"
              onClick={() => handleRemoveGameClick(r)}
            >
              Remove
            </Button>
          ) : null}
        </Stack>
      );
    }
    if (
      !isMobile &&
      r.mode === 'teams' &&
      !r.gameStarted &&
      !r.confirmingPlayers &&
      r.activePlayers.length < MAX_PLAYERS_IN_TEAMS
    ) {
      if (!r.activePlayers.includes(profileMe.id)) {
        return (
          <Stack direction={'row'} spacing={1}>
            <Button
              variant="contained"
              sx={{ width: 80 }}
              onClick={() => joinTeamGameClick(r.gameId, r.mode)}
              disabled={
                r.activePlayers.length >= MAX_PLAYERS_IN_TEAMS ||
                disabledTeamJoinButton
              }
            >
              Join
            </Button>
            {r.activePlayers.length === 0 && r.createdBy === profileMe.id ? (
              <Button
                sx={{ width: 80 }}
                variant="contained"
                onClick={() => handleRemoveGameClick(r)}
              >
                Remove
              </Button>
            ) : null}
          </Stack>
        );
      } else {
        return (
          <Stack direction={'row'} spacing={1}>
            <Button
              variant="contained"
              sx={{ width: 80 }}
              onClick={() => handlePickTeamsClick(r.gameId)}
              disabled={
                r.activePlayers.length >= MAX_PLAYERS_IN_TEAMS ||
                openTeamPickingDialog
              }
            >
              Pick Teams
            </Button>
          </Stack>
        );
      }
    }
    if (
      !isMobile &&
      (r.mode === 'match' || r.mode === 'sit-n-go') &&
      r.activePlayers.includes(profileMe.id) &&
      !r.gameStarted
    ) {
      return (
        <Stack direction={'row'} spacing={1}>
          <Button
            variant="contained"
            onClick={() => handleLeaveGameClick(r.gameId, profileMe.id)}
          >
            Leave Game
          </Button>
          {r.mode === 'sit-n-go' &&
          r.activePlayers.length > 1 &&
          r.createdBy === profileMe.id ? (
            <Tooltip
              placement="top"
              title="Start early with currently joined players"
            >
              <Button
                variant="contained"
                onClick={() => handleForceStartClick(r)}
              >
                Force Start
              </Button>
            </Tooltip>
          ) : null}
        </Stack>
      );
    }
    if (
      (r.mode === 'solo' ||
        r.mode === 'match' ||
        r.mode === 'sit-n-go' ||
        r.mode === 'teams') &&
      r.gameStarted &&
      !r.gameOver &&
      !r.activePlayers.find((profileId: string) => profileId === profileMe.id)
    ) {
      return (
        <Button
          variant="contained"
          sx={{
            backgroundColor: 'secondary.main',
            color: 'secondary.contrastText',
          }}
          onClick={() => handleSpectateClick(r.gameId)}
        >
          Spectate
        </Button>
      );
    }
  };

  const handleUsernameClick = async (e: any, player: Player) => {
    e.stopPropagation();
    await track(profileMe.id, 'lobby.username.link', player.username);
    navigate(`/profile/overview/${player.profileId}`);
  };

  const getRatingNextToUsername = (
    mode: Mode,
    gameOver: boolean,
    player: Player
  ) => {
    const formatRating = (rating: number) => {
      const rounded = Math.round(rating);
      return `${rounded > 0 ? '+' : ''}${rounded}`;
    };

    if (gameOver) {
      return formatRating(player.rankedRatingDiff || 0);
    }
    return (player.rankedMatchGamesPlayed || 0) +
      (player.rankedSitAndGoGamesPlayed || 0) <
      10
      ? 'calibrating'
      : player.rankedRating;
  };

  const ratingColor = (gameOver: boolean, player: Player) => {
    if (gameOver) {
      if (Math.round(player.rankedRatingDiff || 0) > 0)
        return theme.palette.success.main;
      if (Math.round(player.rankedRatingDiff || 0) < 0)
        return theme.palette.error.main;
      return theme.palette.warning.main;
    }
    return theme.palette.ranked.main;
  };

  const getJoinedPlayers = (game: Game) => {
    if (!game.players || Object.keys(game.players).length === 0) return '--';
    const rankedPlayers = assignRanks(
      Object.values(game.players).sort((a, b) => a.parScore - b.parScore)
    );
    return (
      <Stack width={'100%'}>
        {rankedPlayers.slice(0, 20).map((p, i) => (
          <Link
            key={i}
            component="button"
            variant="body2"
            onClick={(e) => handleUsernameClick(e, p)}
            textAlign={'left'}
          >
            <Stack direction={'row'} sx={{ whiteSpace: 'nowrap' }}>
              {game.mode === 'sit-n-go'
                ? `${game.gameOver ? p.rank : i + 1}.`
                : ''}{' '}
              {game.mode === 'teams'
                ? `${
                    game.teams.teamA.members.includes(p.profileId)
                      ? 'A.'
                      : game.teams.teamB.members.includes(p.profileId)
                      ? 'B.'
                      : ''
                  }`
                : ''}{' '}
              {p.username}
              {(game.mode === 'match' || game.mode === 'sit-n-go') &&
              game.ranked ? (
                <Stack direction={'row'}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: theme.palette.ranked.main }}
                  >
                    &nbsp;(
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: ratingColor(game.gameOver, p) }}
                  >
                    {getRatingNextToUsername(game.mode, game.gameOver, p)}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: theme.palette.ranked.main }}
                  >
                    )
                  </Typography>
                  {game.matchMaking &&
                  game.matchMakingSeededBy === p.profileId ? (
                    <Tooltip title="Seeded this match" placement="top">
                      <Box
                        sx={{
                          marginLeft: 1,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <FaSeedling
                          style={{
                            fontSize: '14px',
                            color: theme.palette.success.main,
                          }}
                        />
                      </Box>
                    </Tooltip>
                  ) : null}
                </Stack>
              ) : null}
            </Stack>
          </Link>
        ))}
        {rankedPlayers.length > 20 ? (
          <Link
            component="button"
            variant="body1"
            onClick={(e) =>
              handleJoinPlayersMore(
                e,
                rankedPlayers,
                game.gameOver,
                game.ranked
              )
            }
            textAlign={'left'}
          >
            <Stack
              direction={'row'}
              sx={{ whiteSpace: 'nowrap', color: theme.palette.primary.main }}
            >
              ...{rankedPlayers.length - 20} more players
            </Stack>
          </Link>
        ) : null}
      </Stack>
      //   <Tooltip placement="top" title={mmrCalc(game.players)}>
      //     <Chip
      //       label="MMR"
      //       sx={{ textAlign: 'right', color: theme.palette.ranked.main  }}
      //     ></Chip>
      //   </Tooltip>
      // </Stack>
    );
  };

  const handleJoinPlayersMore = (
    event: any,
    players: Player[],
    gameOver: boolean,
    ranked: boolean
  ) => {
    setClickedJoinedPlayersMore({ players, gameOver, ranked });
    setAnchorElJoinedPlayersMore(event.currentTarget);
    setOpenJoinedPlayersMorePopover(true);
  };

  const mmrCalc = (players: Record<string, Player>) => {
    const otherPlayerRating = getOtherPlayerMatchRating(players);
    if (!otherPlayerRating) return 'N/A';
    return (
      <div>
        {calcMatchRating(
          'win',
          profileMe.rankedMatchGamesPlayed,
          profileMe.rankedMatchRating,
          otherPlayerRating
        )}
      </div>
    );
  };

  const getOtherPlayerMatchRating = (players: Record<string, Player>) => {
    const otherPlayerProfileId = Object.keys(players).find(
      (pid) => pid !== profileMe.id
    );
    if (!otherPlayerProfileId) return undefined;
    return players[otherPlayerProfileId].rankedMatchRating;
  };

  const isJoined = (game: Game) => {
    if (!game.activePlayers) return false;
    return (
      Object.keys(game.activePlayers).includes(profileMe.id) && !game.gameOver
    );
  };

  const handleViewResultsClick = async (game: Game) => {
    const res = await fetchGet(`/games/result/${game.gameId}`);
    res.game.questionSet = res.questionSet;
    setGameResults(res.game);
    setOpenScorecardModal(true);
  };

  const results = (game: Game) => (
    <Stack>
      <Typography variant="body2">
        {utcToLocalDateTime(game.createdAt)}
      </Typography>
      <Link
        sx={{ cursor: 'pointer' }}
        onClick={() => handleViewResultsClick(game)}
      >
        View Results
      </Link>
      {game.hasReplay ? (
        <Link
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate(`/replay/${game.gameId}`)}
        >
          Replay
        </Link>
      ) : null}
    </Stack>
  );

  const stackName = (r: Game) => (
    <Tooltip placement="top" title={`Game ID: ${r.gameId}`}>
      <Stack>
        <Stack direction="row" spacing={1}>
          {r.ranked ? (
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                color: theme.palette.ranked.main,
              }}
            >
              Ranked
            </Typography>
          ) : null}
          {r.ghostChallenge ? (
            <Tooltip title="This game was a Ghost Challenge" placement="bottom">
              <BiGhost
                style={{
                  fontSize: '18px',
                  color: theme.palette.warning.main,
                }}
              />
            </Tooltip>
          ) : null}
          {r.matchMaking ? (
            <Tooltip title="This game was Searched" placement="bottom">
              <SearchIcon
                style={{
                  fontSize: '22px',
                  color: theme.palette.dailies.main,
                }}
              />
            </Tooltip>
          ) : null}
          {r.handicap ? (
            <Tooltip
              title="A handicap was set for this game"
              placement="bottom"
            >
              <Box
                sx={{
                  marginLeft: 1,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <BalanceIcon
                  style={{
                    fontSize: '20px',
                    color: theme.palette.handicap.main,
                  }}
                />
              </Box>
            </Tooltip>
          ) : null}
          {r.variant === 'survival' && r.survivalMode === 'endurance' ? (
            <Tooltip title="Survival - Endurance" placement="bottom">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <GiRaiseZombie
                  style={{
                    fontSize: '20px',
                    color: theme.palette.survival.main,
                  }}
                />
              </Box>
            </Tooltip>
          ) : null}
          {r.variant === 'survival' && r.survivalMode === 'challenge' ? (
            <Tooltip title="Survival - Challenge Tower" placement="bottom">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <GiStoneTower
                  style={{
                    fontSize: '16px',
                    color: theme.palette.survival.main,
                  }}
                />
              </Box>
            </Tooltip>
          ) : null}
          {r.demons ? (
            <Tooltip title="Seeded this match" placement="top">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <GiDevilMask
                  style={{
                    fontSize: '18px',
                    color: theme.palette.demon.main,
                  }}
                />
              </Box>
            </Tooltip>
          ) : null}
        </Stack>
        {r.customGameName || 'N/A'}
      </Stack>
    </Tooltip>
  );

  const gameType = (r: Game) =>
    r.demo
      ? '--'
      : r.gameType === 'casual'
      ? 'Cas.'
      : r.gameType === 'pro'
      ? 'Pro'
      : r.gameType;

  const categoryAndDemo = (r: Game) =>
    r.demo || r.categorySet === 'Demo'
      ? 'Demo'
      : r.categorySet === 'none' && r.categorySelections
      ? r.categorySelections.join(' ')
      : CATEGORY_SETS_LABEL[r.categorySet];

  const handlePaginateOnChange = (event: any, pageNum: number) => {
    setPageNum(pageNum);
  };

  const getStatus = (r: Game) => {
    if (
      r.mode === 'sit-n-go' &&
      !r.gameStarted &&
      r.activePlayers &&
      !r.confirmingPlayers
    ) {
      return `${r.activePlayers.length} / ${r.sitAndGoMaxPlayers}`;
    }
    if (
      r.mode === 'teams' &&
      !r.gameStarted &&
      r.activePlayers &&
      !r.confirmingPlayers
    ) {
      return `${r.activePlayers.length} / ${MAX_PLAYERS_IN_TEAMS}`;
    }
    if (!r.gameStarted && r.confirmingPlayers) {
      return 'Starting';
    }
    if (r.gameStarted && !r.gameOver) {
      return 'In progress';
    }
    return r.status;
  };

  const pagination = (
    <Pagination
      count={10}
      size={xsBreakpoint ? 'small' : 'medium'}
      onChange={handlePaginateOnChange}
      sx={{ display: 'flex', justifyContent: 'right' }}
    />
  );

  const lobbyCards = (
    <Grid container spacing={1}>
      {!noGamesToDisplay ? (
        lobbyGamesPaged?.map((r: Game) => (
          <Grid item key={r.gameId} xs={6}>
            <Card
              variant="outlined"
              // key={r.gameId}
              sx={{
                border: isJoined(r) ? '2px solid' : 'none',
                borderColor: isJoined(r) ? 'secondary.main' : 'none',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              {isJoined(r) ? (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: 12,
                    padding: '2px',
                    float: 'right',
                    backgroundColor: 'secondary.main',
                    color: 'secondary.contrastText',
                  }}
                >
                  Joined
                </Typography>
              ) : null}
              <CardHeader
                avatar={
                  r.status === 'Finished' ? (
                    <Avatar
                      sx={{
                        backgroundColor: theme.palette.tertiary.main,
                      }}
                    >
                      <DoneIcon
                        sx={{
                          color: 'success.main',
                          textShadow: '0 0 18px #fff, 0 0 17px #fff',
                        }}
                      />
                    </Avatar>
                  ) : null
                }
                title={stackName(r)}
              />
              <CardContent sx={{ pt: 0 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  color="text.secondary"
                >
                  <Typography variant="overline">
                    {MODE_ABBR[r.mode]}
                  </Typography>
                  <Typography variant="overline"> | </Typography>
                  <Typography variant="overline">{gameType(r)}</Typography>
                  <Typography variant="overline"> | </Typography>
                  <Typography variant="overline">
                    {categoryAndDemo(r)}
                  </Typography>
                </Stack>
                {getJoinedPlayers(r)}
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                {r.status === 'Finished' ? results(r) : actionButton(r)}
              </CardActions>
            </Card>
          </Grid>
        ))
      ) : (
        <Card>
          <CardContent style={{ textAlign: 'center' }}>
            No games to display
          </CardContent>
        </Card>
      )}
    </Grid>
  );

  const lobbyTable = (
    <TableContainer component={Paper}>
      <Table aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell width={'15%'}>Name</TableCell>
            <TableCell width={'5%'}>Mode</TableCell>
            <TableCell width={'7%'}>
              <Tooltip placement="top" title="Difficulty">
                <span>Diff.</span>
              </Tooltip>
            </TableCell>
            <TableCell width={'10%'}>Category</TableCell>
            <TableCell width={'20%'}>Players</TableCell>
            <TableCell width={'10%'}>Status</TableCell>
            <TableCell width={'15%'}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!noGamesToDisplay ? (
            lobbyGamesPaged?.map((r: Game) => (
              <Box key={r.gameId} width="100%" display="contents">
                {isJoined(r) ? (
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: 12,
                      position: 'absolute',
                      backgroundColor: 'secondary.main',
                      color: 'secondary.contrastText',
                    }}
                  >
                    Joined
                  </Typography>
                ) : null}
                <TableRow
                  sx={{
                    border: isJoined(r) ? '2px solid' : 'none',
                    borderColor: isJoined(r) ? 'secondary.main' : 'none',
                  }}
                >
                  <TableCell>{stackName(r)}</TableCell>
                  <TableCell>
                    <Tooltip
                      placement="top-end"
                      title={MODE_DISPLAY_NAME[r.mode] || ''}
                    >
                      <span>{MODE_ABBR[r.mode]}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{gameType(r)}</TableCell>
                  <TableCell>{categoryAndDemo(r)}</TableCell>
                  <TableCell>{getJoinedPlayers(r)}</TableCell>
                  <TableCell>{getStatus(r)}</TableCell>
                  <TableCell>
                    <Stack direction={'row'} justifyContent={'space-between'}>
                      {r.status === 'Finished' ? results(r) : actionButton(r)}
                      {profileMe.admin ? (
                        <Tooltip title="Resets players" placement="top">
                          <Button
                            size="small"
                            onClick={(e) =>
                              handleAdminClick(e, r.activePlayers)
                            }
                          >
                            admin
                          </Button>
                        </Tooltip>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              </Box>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={8} style={{ textAlign: 'center' }}>
                No games to display
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [adminClickedActivePlayers, setAdminClickedActivePlayers] =
    useState<string[]>();

  const handleAdminClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    activePlayers: string[]
  ) => {
    setAnchorEl(event.currentTarget);
    setAdminClickedActivePlayers(activePlayers);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const [openPlayerStatusesDialog, setOpenPlayerStatusesDialog] =
    useState(false);

  return (
    <>
      {adminClickedActivePlayers ? (
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Stack spacing={2} padding={1}>
            <Button
              variant="contained"
              size="small"
              onClick={() => handleResetPlayers(adminClickedActivePlayers)}
            >
              Reset all players
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handlePlayerStatusesClick}
            >
              Player Statuses
            </Button>
          </Stack>
        </Popover>
      ) : null}
      {adminClickedActivePlayers && socket ? (
        <PlayerStatusesDialog
          activePlayers={adminClickedActivePlayers}
          close={() => setOpenPlayerStatusesDialog(false)}
          open={openPlayerStatusesDialog}
          socket={socket}
        />
      ) : null}
      {clickedJoinedPlayersMore ? (
        <JoinedPlayersMorePopover
          anchorEl={anchorElJoinedPlayersMore}
          close={() => setOpenJoinedPlayersMorePopover(false)}
          gameOver={clickedJoinedPlayersMore.gameOver}
          handleUsernameClick={handleUsernameClick}
          open={openJoinedPlayersMorePopover}
          players={clickedJoinedPlayersMore.players}
          ranked={clickedJoinedPlayersMore.ranked}
        />
      ) : null}
      {gameResults && profileMe.id ? (
        <ScoreCardModal
          bannedCategories={gameResults.bannedCategories}
          categorySet={gameResults.categorySet}
          gameType={gameResults.gameType}
          gameId={gameResults.gameId}
          handicap={gameResults.handicap}
          onClose={() => setOpenScorecardModal(false)}
          openScordcardModal={openScorecardModal}
          league={gameResults.league}
          mode={gameResults.mode}
          players={gameResults.players as Record<string, DetailedPlayer>}
          profileId={
            profileMe
              ? profileMe.id
              : Object.values(gameResults.players)[0].profileId
          }
          questionSet={gameResults.questionSet}
          ranked={gameResults.ranked}
          seededBy={gameResults.matchMakingSeededBy}
          survivalMode={gameResults.survivalMode}
          teams={gameResults.teams}
          variant={gameResults.variant}
        />
      ) : null}
      <Stack direction={'row'} mb={1}>
        <Box width="100%" sx={{ justifyContent: 'right' }}>
          {pagination}
        </Box>
      </Stack>
      {xsBreakpoint ? lobbyCards : lobbyTable}
    </>
  );
};
